import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { header } from '../components/header.encry';
import useAuth from '../components/useAuth';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Card,
  CardContent,
  Stack,
  Chip,
  Collapse,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DirectionsIcon from '@mui/icons-material/Directions';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const STATE_MAP = {
  MA001: 'Casablanca',
  MA002: 'DAR BOUAZZA',
  MA003: 'Bouskoura',
  MA004: 'Mohamedia',
  MA005: 'Benselimane',
  MA006: 'Rabat',
  MA007: 'Sale',
  MA008: 'Temara',
  MA009: 'Marrakech',
  MA010: 'Ben Guerrir',
  MA011: 'Agadir',
  MA012: 'Tit Melil',
  MA013: 'Sidi Rahal',
  MA014: 'Kenitra',
  MA015: 'El Jadida',
  MA016: 'Safi',
  MA017: 'Tanger'
};

const STATUS_LABELS = {
  'delivery-inroute': 'Livraison en cours',
  completed: 'Terminee',
  gone: 'Terminee',
  accepte: 'Acceptee',
  accepted: 'Acceptee',
  problem: 'Probleme',
  'not-available': 'Indisponible',
  processing: 'En preparation',
  pending: 'En attente'
};

const STATUS_STYLES = {
  'delivery-inroute': { background: '#DBEAFE', border: '#60A5FA', text: '#1D4ED8', chip: '#3B82F6' },
  completed: { background: '#DCFCE7', border: '#4ADE80', text: '#166534', chip: '#16A34A' },
  gone: { background: '#DCFCE7', border: '#4ADE80', text: '#166534', chip: '#16A34A' },
  accepted: { background: '#FEF3C7', border: '#FBBF24', text: '#92400E', chip: '#D97706' },
  accepte: { background: '#FEF3C7', border: '#FBBF24', text: '#92400E', chip: '#D97706' },
  problem: { background: '#FEE2E2', border: '#F87171', text: '#B91C1C', chip: '#DC2626' },
  'not-available': { background: '#FEE2E2', border: '#FCA5A5', text: '#991B1B', chip: '#DC2626' },
  pending: { background: '#F3F4F6', border: '#D1D5DB', text: '#374151', chip: '#6B7280' },
  processing: { background: '#E0F2FE', border: '#7DD3FC', text: '#075985', chip: '#0284C7' }
};

const STATUS_OPTIONS = [
  'completed',
  'problem',
  'not-available',
  'delivery-inroute',
  'accepted',
  'ongoing',
  'cancelled',
  'returned',
  'processing',
  'pending',
  'refunded',
  'failed',
  'en-cours-de-produ',
  'en-cours-de-produ-mp'
];

const isWebsiteOrder = (order) => String(order?.productType || '').trim().toLowerCase() === 'site';

export default function DriverOrdersView() {
  const { roles } = useAuth();
  const [orders, setOrders] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [pendingActionLabel, setPendingActionLabel] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCourierProfile = roles.includes('driver') && !roles.includes('admin') && !roles.includes('manager');

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
      const res = await axios.get(`${API_URL}/orders/driver`, header());
      const data = res?.data?.orders || res?.data || [];
      const fetchedOrders = Array.isArray(data) ? data : [];
      setOrders(isCourierProfile ? fetchedOrders.filter(isWebsiteOrder) : fetchedOrders);
    } catch (error) {
      console.error('Error fetching driver orders', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const humanLabel = (status) => {
    if (!status) return '';
    if (STATUS_LABELS[status]) return STATUS_LABELS[status];
    const text = String(status).replace(/[-_]/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const getStatusStyle = (statusKey) => {
    const key = String(statusKey || '').toLowerCase();
    if (STATUS_STYLES[key]) return STATUS_STYLES[key];
    if (key.includes('deliver') || key.includes('inroute')) return STATUS_STYLES['delivery-inroute'];
    if (key.includes('completed') || key.includes('termin')) return STATUS_STYLES.completed;
    if (key.includes('accept')) return STATUS_STYLES.accepted;
    if (key.includes('problem')) return STATUS_STYLES.problem;
    return { background: '#F9FAFB', border: '#E5E7EB', text: '#374151', chip: '#6B7280' };
  };

  const getOrderStatusKey = (order) => {
    if (!order) return '';
    const status = (order.orderStatus || order.status || order?.affected?.status || '').toString().toLowerCase();
    return status.replace(/_/g, '-');
  };

  const isDolibarrTicketOrder = (order) => getOrderStatusKey(order) === 'dolibarr-ticket';

  const computeStats = (list) => {
    const stats = { delivery_inroute: 0, completed: 0, accepted: 0, others: 0 };
    (Array.isArray(list) ? list : []).forEach((order) => {
      const key = getOrderStatusKey(order);
      if (key.includes('delivery-inroute') || key === 'delivery-inroute') stats.delivery_inroute += 1;
      else if (key === 'completed' || key === 'gone' || key === 'terminee' || key === 'delivered' || key.includes('completed')) stats.completed += 1;
      else if (key === 'accepted' || key === 'accepte' || key.includes('accept')) stats.accepted += 1;
      else stats.others += 1;
    });
    return stats;
  };

  const isOrderCompleted = (order) => {
    const key = getOrderStatusKey(order);
    if (!key) return false;
    return key === 'completed' || key === 'gone' || key === 'terminee' || key === 'delivered' || key.includes('completed') || key.includes('termin');
  };

  const getDeliveryDate = (order) => {
    if (!order) return '-';

    const meta = order.meta_data || order.meta || order?.meta_data?.woo || null;
    if (meta && meta.woo && Array.isArray(meta.woo)) {
      const found = meta.woo.find((item) => String(item.key).toLowerCase() === 'date de livraison' || String(item.key).toLowerCase() === '_orddd_delivery_date');
      if (found) return found.value || '-';
    }

    if (Array.isArray(order.meta_data)) {
      const found = order.meta_data.find((item) => String(item.key).toLowerCase() === 'date de livraison' || String(item.key).toLowerCase() === '_orddd_delivery_date');
      if (found) return found.value || '-';
    }

    if (Array.isArray(order?.customer_data?.raw?.meta_data)) {
      const found = order.customer_data.raw.meta_data.find((item) => String(item.key).toLowerCase() === 'date de livraison' || String(item.key).toLowerCase() === '_orddd_delivery_date');
      if (found) return found.value || '-';
    }

    return order.orderDate || order.order_date || '-';
  };

  const getCity = (order) => {
    if (!order) return '-';
    if (order?.customer_data?.shipping?.city) return order.customer_data.shipping.city;
    if (order?.shipping?.city) return order.shipping.city;
    if (order?.customer_data?.raw?.shipping?.city) return order.customer_data.raw.shipping.city;

    const stateCode = order?.customer_data?.shipping?.state || order?.shipping?.state || order?.customer_data?.raw?.shipping?.state;
    if (!stateCode) return '-';

    const code = String(stateCode).trim();
    return STATE_MAP[code] || code;
  };

  const openDetails = (order) => {
    setSelectedOrder(order);
    setBillingOpen(false);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setSelectedOrder(null);
    setBillingOpen(false);
    setDetailsOpen(false);
  };

  const normalizePhone = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/[^0-9+]/g, '');
    if (/^0[0-9]+$/.test(digits)) return digits.replace(/^0/, '212');
    if (digits.startsWith('+')) return digits.replace('+', '');
    return digits;
  };

  const getShippingPhone = (order) => {
    if (!order) return '';

    const metaArr = Array.isArray(order.meta_data) ? order.meta_data : (order?.meta_data?.woo || []);
    const metaPhone = Array.isArray(metaArr)
      ? metaArr.find((item) => String(item.key).toLowerCase().includes('shipping_phonenumber') || String(item.key).toLowerCase().includes('phone'))
      : null;

    if (metaPhone?.value) return metaPhone.value;
    if (order?.customer_data?.shipping?.shipping_phonenumber || order?.customer_data?.shipping?.phone) {
      return order.customer_data.shipping.shipping_phonenumber || order.customer_data.shipping.phone;
    }
    if (order?.shipping?.shipping_phonenumber || order?.shipping?.phone) {
      return order.shipping.shipping_phonenumber || order.shipping.phone;
    }
    return order.clientPhone || '';
  };

  const getShippingName = (order) => {
    if (!order) return '';
    const shipping = order?.customer_data?.shipping || order?.shipping || {};
    const first = shipping.first_name || '';
    const last = shipping.last_name || '';
    return `${first} ${last}`.trim() || order.CustomerName || '';
  };

  const getShippingAddress = (order) => {
    if (!order) return '';
    const shipping = order?.customer_data?.shipping || order?.shipping || {};
    return [shipping.address_1, shipping.address_2, shipping.city].filter(Boolean).join(', ');
  };

  const openWhatsApp = (rawPhone) => {
    const number = normalizePhone(rawPhone);
    if (!number) return;
    window.open(`https://wa.me/${number}`, '_blank');
  };

  const openCall = (rawPhone) => {
    if (!rawPhone) return;
    window.location.href = `tel:${String(rawPhone).replace(/\s+/g, '')}`;
  };

  const openMaps = (order) => {
    const address = getShippingAddress(order) || getDeliveryDate(order) || '';
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const extractTextValue = (value) => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value);
    return '';
  };

  const getOrderNote = (order) => {
    if (!order) return '';

    const directCandidates = [
      order.delivery_note,
      order.driver_note,
      order.note_delivery,
      order.note_driver,
      order.note_livreur,
      order.customer_note,
      order.note,
      order.notes,
      order?.affected?.note,
      order?.affected?.delivery_note,
      order?.affected?.driver_note,
      order?.customer_data?.note,
      order?.customer_data?.customer_note,
      order?.customer_data?.shipping?.note,
      order?.shipping?.note
    ];

    const directMatch = directCandidates.map(extractTextValue).find(Boolean);
    if (directMatch) return directMatch;

    const metaArr = Array.isArray(order.meta_data) ? order.meta_data : (order?.meta_data?.woo || []);
    if (Array.isArray(metaArr)) {
      const note = metaArr.find((item) => {
        const key = String(item.key || '').toLowerCase();
        return key.includes('note') || key.includes('customer_note') || key.includes('delivery_note') || key.includes('driver_note');
      });
      if (note?.value) return note.value;
    }

    if (Array.isArray(order?.customer_data?.raw?.meta_data)) {
      const note = order.customer_data.raw.meta_data.find((item) => {
        const key = String(item.key || '').toLowerCase();
        return key.includes('note') || key.includes('customer_note') || key.includes('delivery_note') || key.includes('driver_note');
      });
      if (note?.value) return note.value;
    }

    return '';
  };

  const getBillingName = (order) => {
    if (!order) return '';
    const billing = order?.customer_data?.billing || order?.billing || {};
    return `${billing.first_name || ''}${billing.last_name ? ` ${billing.last_name}` : ''}`.trim() || billing.name || '';
  };

  const getBillingPhone = (order) => {
    if (!order) return '';
    const billing = order?.customer_data?.billing || order?.billing || {};
    return billing.phone || billing.billing_phone || '';
  };

  const computeProductsTotal = (order) => {
    const products = Array.isArray(order?.products) ? order.products : [];
    return products.reduce((sum, product) => {
      const qty = Number(product.quantity) || 1;
      const price = parseFloat(String(product.price || '0').replace(/[^0-9.-]+/g, '')) || 0;
      return sum + qty * price;
    }, 0);
  };

  const getOrderTotal = (order) => {
    if (!order) return 0;
    if (order.total) return Number(order.total) || parseFloat(String(order.total).replace(/[^0-9.-]+/g, '')) || 0;
    if (order.order_total) return Number(order.order_total) || 0;
    return computeProductsTotal(order);
  };

  const getProductCount = (order) => {
    const products = Array.isArray(order?.products) ? order.products : [];
    if (!products.length) return Number(order?.productsCount) || 0;
    return products.reduce((sum, product) => sum + (Number(product?.quantity) || 1), 0);
  };

  const openStatusDialog = () => {
    if (!selectedOrder) return;
    if (isOrderCompleted(selectedOrder)) {
      alert('Cette commande est deja terminee.');
      return;
    }

    setNewStatus(selectedOrder.orderStatus || selectedOrder.status || '');
    setStatusDialogOpen(true);
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setNewStatus('');
  };

  const askStatusChange = (order, status) => {
    if (!order || !status) return;
    if (isOrderCompleted(order)) {
      alert('Cette commande est deja terminee.');
      return;
    }

    setSelectedOrder(order);
    setNewStatus(status);
    if (status === 'ongoing') setPendingActionLabel('Je la livre');
    else if (status === 'completed') setPendingActionLabel('Termine');
    else if (status === 'problem') setPendingActionLabel('Probleme');
    else setPendingActionLabel(humanLabel(status));
    setStatusConfirmOpen(true);
  };

  const confirmUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return;

    try {
      const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
      const orderId = selectedOrder._id || selectedOrder.id || undefined;
      const userId = sessionStorage.getItem('userId');

      if ((newStatus === 'ongoing' || newStatus === 'completed') && orderId && userId) {
        await axios.patch(`${API_URL}/orders/${orderId}/driver/${userId}`, { status: newStatus }, header());
      } else {
        const payload = {
          orderId,
          orderCode: selectedOrder.orderCode || selectedOrder.orderNumber || (selectedOrder.number ? String(selectedOrder.number) : undefined),
          status: newStatus
        };
        await axios.post(`${API_URL}/orders/woo/status/driver`, payload, header());
      }

      await fetchOrders();
      setStatusConfirmOpen(false);
      closeStatusDialog();
      setPendingActionLabel('');
      alert('Status mis a jour');
    } catch (error) {
      console.error('Failed to update status', error);
      alert('Echec de mise a jour du status');
    }
  };

  const getOrderId = (order) => order && (order.orderNumber || order.orderCode || order?._id?.$oid || order?._id || '-');

  const roleScopedOrders = Array.isArray(orders)
    ? orders.filter((order) => !isCourierProfile || isWebsiteOrder(order))
    : [];

  const displayedOrders = roleScopedOrders.filter(
    (order) => !isDolibarrTicketOrder(order) && (showCompleted ? true : !isOrderCompleted(order))
  );

  const getQuickActions = (order) => {
    const status = getOrderStatusKey(order);

    if (isOrderCompleted(order)) return [];

    if (status === 'delivery-inroute') {
      return [
        { label: 'Termine', status: 'completed', color: 'success' },
        { label: 'Probleme', status: 'problem', color: 'error' }
      ];
    }

    if (status === 'accepted' || status === 'accepte') {
      return [
        { label: 'Je la livre', status: 'ongoing', color: 'primary' }
      ];
    }

    if (['ongoing', 'processing', 'pending', 'en-cours-de-produ', 'en-cours-de-produ-mp'].includes(status)) {
      return [
        { label: 'Termine', status: 'completed', color: 'success' },
        { label: 'Probleme', status: 'problem', color: 'error' }
      ];
    }

    if (status === 'problem' || status === 'not-available') {
      return [
        { label: 'En route', status: 'delivery-inroute', color: 'primary' },
        { label: 'Termine', status: 'completed', color: 'success' }
      ];
    }

    return [];
  };

  const stats = computeStats(roleScopedOrders.filter((order) => !isDolibarrTicketOrder(order)));

  return (
    <Container sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Commandes coursier</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={fetchOrders} disabled={loading}>
            {loading ? 'Actualisation...' : 'Rafraichir'}
          </Button>
          <Button size="small" variant={showCompleted ? 'outlined' : 'contained'} onClick={() => setShowCompleted((value) => !value)}>
            {showCompleted ? 'Masquer terminees' : 'Afficher terminees'}
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 1, backgroundColor: '#3B82F6', color: '#fff' }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>{STATUS_LABELS['delivery-inroute']}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.delivery_inroute}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 1, backgroundColor: '#16A34A', color: '#fff' }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>{STATUS_LABELS.completed}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 1, backgroundColor: '#D97706', color: '#fff' }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)' }}>{STATUS_LABELS.accepted}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.accepted}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 1, backgroundColor: '#E5E7EB', color: '#111827' }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: 'rgba(17,24,39,0.7)' }}>Autres</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.others}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && displayedOrders.length === 0 && (
        <Typography variant="body1">Aucune commande disponible.</Typography>
      )}

      {!loading && displayedOrders.length > 0 && (
        <Grid container spacing={2}>
          {displayedOrders.map((order) => {
            const statusKey = getOrderStatusKey(order);
            const statusStyle = getStatusStyle(statusKey);
            const note = getOrderNote(order);
            const statusLabel = statusKey === 'delivered'
              ? STATUS_LABELS.completed
              : (STATUS_LABELS[statusKey] || humanLabel(statusKey) || '-');
            const quickActions = getQuickActions(order);

            return (
              <Grid item xs={12} sm={6} lg={4} key={order._id || order.orderCode || order.orderNumber || Math.random()}>
                <Card
                  sx={{
                    height: '100%',
                    border: `2px solid ${statusStyle.border}`,
                    backgroundColor: statusStyle.background,
                    borderRadius: 3,
                    boxShadow: '0 12px 24px rgba(15,23,42,0.08)'
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" sx={{ color: statusStyle.text, fontWeight: 800, letterSpacing: 1 }}>
                          {getOrderId(order)}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>
                          {getShippingName(order) || 'Client non renseigne'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4B5563', mt: 0.5, fontWeight: 600 }}>
                          {getDeliveryDate(order)}
                        </Typography>
                      </Box>
                      <Chip
                        label={statusLabel}
                        size="small"
                        sx={{
                          backgroundColor: statusStyle.chip,
                          color: '#fff',
                          fontWeight: 700
                        }}
                      />
                    </Box>

                    <Stack spacing={1.25}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          p: 1.25,
                          borderRadius: 2,
                          backgroundColor: 'rgba(255,255,255,0.84)'
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Ville
                          </Typography>
                          <Typography sx={{ fontWeight: 700 }} noWrap>
                            {getCity(order)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Produits
                          </Typography>
                          <Typography sx={{ fontWeight: 800 }}>
                            {getProductCount(order)}
                          </Typography>
                        </Box>
                      </Box>

                      {note ? (
                        <Box sx={{ p: 1.25, borderRadius: 2, backgroundColor: '#FFF7D6', border: '1px solid #FDE68A' }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#92400E', mb: 0.5 }}>
                            Note livraison
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#78350F', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {note.length > 140 ? `${note.substring(0, 140)}...` : note}
                          </Typography>
                        </Box>
                      ) : null}
                    </Stack>

                    {quickActions.length > 0 ? (
                      <Stack direction={isMobile ? 'column' : 'row'} spacing={1} sx={{ mt: 2 }}>
                        {quickActions.map((action) => (
                          <Button
                            key={`${getOrderId(order)}-${action.status}`}
                            variant="contained"
                            color={action.color}
                            fullWidth
                            onClick={() => askStatusChange(order, action.status)}
                            sx={{ fontWeight: 700 }}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </Stack>
                    ) : null}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                      <Button variant="text" startIcon={<VisibilityIcon />} onClick={() => openDetails(order)} sx={{ fontWeight: 700 }}>
                        Voir details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="h6">Details de la commande</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{getOrderId(selectedOrder)}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder ? (
            <Box sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{getShippingName(selectedOrder)}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{getShippingAddress(selectedOrder)}</Typography>
                  {getShippingPhone(selectedOrder) ? (
                    <Typography component="a" href={`tel:${getShippingPhone(selectedOrder)}`} variant="body2" sx={{ display: 'block', mt: 0.5 }}>
                      {getShippingPhone(selectedOrder)}
                    </Typography>
                  ) : null}
                  {getOrderNote(selectedOrder) ? (
                    <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, backgroundColor: '#FFF7D6', border: '1px solid #FDE68A' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#92400E', mb: 0.5 }}>
                        Note livraison
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#78350F', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {getOrderNote(selectedOrder)}
                      </Typography>
                    </Box>
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    {getShippingPhone(selectedOrder) ? (
                      isMobile ? (
                        <>
                          <IconButton aria-label="call-shipping" color="primary" onClick={() => openCall(getShippingPhone(selectedOrder))} href={`tel:${getShippingPhone(selectedOrder)}`}>
                            <PhoneIcon />
                          </IconButton>
                          <IconButton aria-label="whatsapp-shipping" color="success" onClick={() => openWhatsApp(getShippingPhone(selectedOrder))}>
                            <WhatsAppIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button variant="outlined" startIcon={<PhoneIcon />} onClick={() => openCall(getShippingPhone(selectedOrder))} href={`tel:${getShippingPhone(selectedOrder)}`}>
                            Call
                          </Button>
                          <Button variant="contained" color="success" startIcon={<WhatsAppIcon />} onClick={() => openWhatsApp(getShippingPhone(selectedOrder))}>
                            WhatsApp
                          </Button>
                        </>
                      )
                    ) : null}
                    {isMobile ? (
                      <IconButton aria-label="itinerary" onClick={() => openMaps(selectedOrder)}>
                        <DirectionsIcon />
                      </IconButton>
                    ) : (
                      <Button variant="text" startIcon={<DirectionsIcon />} onClick={() => openMaps(selectedOrder)}>
                        Itinerary
                      </Button>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Chip label={getDeliveryDate(selectedOrder)} size="small" sx={{ fontWeight: 700, backgroundColor: '#DCFCE7', color: '#166534' }} />
                    <Chip
                      label={STATUS_LABELS[getOrderStatusKey(selectedOrder)] || humanLabel(getOrderStatusKey(selectedOrder)) || '-'}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: getStatusStyle(getOrderStatusKey(selectedOrder)).chip,
                        color: '#fff'
                      }}
                    />
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="text"
                        endIcon={<ExpandMoreIcon sx={{ transform: billingOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }} />}
                        onClick={() => setBillingOpen((value) => !value)}
                      >
                        Billing details
                      </Button>
                      <Collapse in={billingOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1, textAlign: { xs: 'left', sm: 'right' } }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{getBillingName(selectedOrder)}</Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>{getBillingPhone(selectedOrder)}</Typography>
                          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                            {getBillingPhone(selectedOrder) ? (
                              <>
                                <IconButton aria-label="call-billing" color="primary" onClick={() => openCall(getBillingPhone(selectedOrder))} href={`tel:${getBillingPhone(selectedOrder)}`}>
                                  <PhoneIcon />
                                </IconButton>
                                <IconButton aria-label="whatsapp-billing" color="success" onClick={() => openWhatsApp(getBillingPhone(selectedOrder))}>
                                  <WhatsAppIcon />
                                </IconButton>
                              </>
                            ) : null}
                          </Stack>
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Produits</Typography>
                  <Stack spacing={1}>
                    {(selectedOrder.products || []).map((product, index) => (
                      <Paper key={index} sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#FFF9C4' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar variant="square" src={product.urlImage} alt={product.title} sx={{ width: 64, height: 64 }} />
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>{`${product.quantity || 1} x ${product.title}`}</Typography>
                            {Array.isArray(product.options) && product.options.length > 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {product.options.map((option) => option.title || option).join(', ')}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontWeight: 700 }}>{product.price} DHS</Typography>
                          <Typography variant="caption" color="text.secondary">Qty: {product.quantity}</Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center', pr: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{(getOrderTotal(selectedOrder) || 0).toFixed(2)} DHS</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Typography>Aucune commande selectionnee.</Typography>
          )}
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
          <Button onClick={closeDetails}>Close</Button>
          <Button variant="contained" color="primary" onClick={openStatusDialog} disabled={isOrderCompleted(selectedOrder)}>
            Update Status
          </Button>
        </Box>
      </Dialog>

      <Dialog open={statusDialogOpen} onClose={closeStatusDialog} maxWidth="xs">
        <DialogTitle>Update order status</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={STATUS_OPTIONS.map((status) => ({ value: status, label: humanLabel(status) }))}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => <li {...props} key={option.value}>{option.label}</li>}
            value={newStatus ? { value: newStatus, label: humanLabel(newStatus) } : null}
            onChange={(event, value) => {
              setNewStatus(value ? value.value : '');
              setPendingActionLabel(value ? value.label : '');
            }}
            renderInput={(params) => <TextField {...params} label="Status" variant="outlined" sx={{ mt: 1 }} />}
            fullWidth
            freeSolo={false}
          />
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
          <Button onClick={closeStatusDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (!newStatus) {
                alert('Select a status');
                return;
              }
              setStatusConfirmOpen(true);
            }}
          >
            Confirm
          </Button>
        </Box>
      </Dialog>

      <Dialog open={statusConfirmOpen} onClose={() => setStatusConfirmOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Etes-vous sur de changer le status vers "{pendingActionLabel || humanLabel(newStatus)}" ?</Typography>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
          <Button onClick={() => setStatusConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              setStatusConfirmOpen(false);
              await confirmUpdateStatus();
            }}
          >
            OK
          </Button>
        </Box>
      </Dialog>
    </Container>
  );
}
